import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

// "+1 (555) 123-0001" / "5551230001" → "+15551230001". The server validates the result.
export const normalizePhone = (v) => {
  const d = String(v).replace(/[^\d+]/g, "");
  if (d.startsWith("+")) return d;
  return d.length === 10 ? `+1${d}` : d.length === 11 && d.startsWith("1") ? `+${d}` : d;
};

// Web phone check for appeal/delete: request a code, then verify → scoped token (never an app login).
export default function PhoneVerify({ purpose, onVerified, intro, phoneLabel, buttonLabel }) {
  const [phone, setPhone] = useState("");
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const id = `pv-${purpose}`;
  const run = async (fn) => { setBusy(true); setError(null); try { await fn(); } catch (e) { setError(e.message); } finally { setBusy(false); } };
  const request = (e) => { e.preventDefault(); run(async () => setSent(await api("/public/phone/request-code", { auth: false, body: { phone: normalizePhone(phone), purpose } }))); };
  const verify = (e) => { e.preventDefault(); run(async () => { const r = await api("/public/phone/verify", { auth: false, body: { phone: sent.phone, purpose, code: code.trim() } }); onVerified(r.token, sent.phone); }); };

  return !sent ? (
    <form onSubmit={request} className="space-y-4" data-testid={`${id}-phone-form`}>
      {intro && <p className="cw-soft text-sm leading-6">{intro}</p>}
      <div><Label htmlFor={`${id}-phone`}>{phoneLabel || "Phone number on your account"}</Label>
        <Input id={`${id}-phone`} type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 0001" className="mt-2 h-12 max-w-xs tabular-nums" data-testid={`${id}-phone-input`} /></div>
      {error && <p role="alert" className="cw-error text-sm" data-testid={`${id}-error`}>{error}</p>}
      <Button type="submit" disabled={busy || normalizePhone(phone).length < 8} className="s-btn-contrast h-12 rounded-full px-6" data-testid={`${id}-send-code-button`}>{busy ? "Sending…" : buttonLabel || "Text me a code"}</Button>
    </form>
  ) : (
    <form onSubmit={verify} className="space-y-4" data-testid={`${id}-code-form`}>
      <p className="cw-soft text-sm">We texted a code to <span className="font-medium text-foreground tabular-nums">{sent.phone}</span>.{" "}
        <button type="button" onClick={() => { setSent(null); setCode(""); setError(null); }} className="underline" data-testid={`${id}-change-phone-button`}>Change number</button></p>
      <div><Label htmlFor={`${id}-code`}>6-digit code</Label>
        <Input id={`${id}-code`} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="mt-2 h-12 max-w-[10rem] text-center text-lg tracking-[0.3em] tabular-nums" data-testid={`${id}-code-input`} /></div>
      {error && <p role="alert" className="cw-error text-sm" data-testid={`${id}-error`}>{error}</p>}
      <Button type="submit" disabled={busy || code.length !== 6} className="h-12 rounded-full px-6" data-testid={`${id}-verify-button`}>{busy ? "Checking…" : "Continue"}</Button>
    </form>
  );
}
